import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppWidget from '../../components/feature/WhatsAppWidget';
import { updateSEO, getBaseUrl } from '../../utils/seo';

const KvkkPage = () => {
  useEffect(() => {
    const baseUrl = getBaseUrl();
    
    // Update meta robots to noindex for legal pages
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex, nofollow');
    
    updateSEO({
      title: 'KVKK & Çerez Politikası | Bursa Sulama Sistemleri',
      description: 'Bursa Sulama Sistemleri kişisel verilerin korunması ve çerez kullanım politikası. KVKK uyarınca kişisel verilerinizin işlenmesi hakkında bilgilendirme.',
      keywords: 'kvkk, çerez politikası, kişisel verilerin korunması, gizlilik politikası',
      canonical: `${baseUrl}/kvkk`
    });

    // Cleanup: reset robots meta when leaving the page
    return () => {
      const robotsMetaCleanup = document.querySelector('meta[name="robots"]');
      if (robotsMetaCleanup) {
        robotsMetaCleanup.setAttribute('content', 'index, follow');
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#14B8A6]/10 to-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-[#0A1628] mb-6">
              KVKK & Çerez Politikası
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
              Kişisel Verilerin Korunması ve Çerez Kullanımı
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-lg max-w-none"
          >
            {/* KVKK Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#0A1628] mb-6">
                Kişisel Verilerin Korunması Kanunu (KVKK) Aydınlatma Metni
              </h2>

              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    1. Veri Sorumlusu
                  </h3>
                  <p>
                    6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK")
                    uyarınca, kişisel verileriniz; veri sorumlusu olarak Bursa
                    Sulama Sistemleri tarafından aşağıda açıklanan kapsamda
                    işlenebilecektir.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    2. Kişisel Verilerin İşlenme Amacı
                  </h3>
                  <p>
                    Toplanan kişisel verileriniz, şirketimiz tarafından sunulan
                    ürün ve hizmetlerden sizleri faydalandırmak için gerekli
                    çalışmaların iş birimlerimiz tarafından yapılması,
                    şirketimiz tarafından sunulan ürün ve hizmetlerin
                    sizlerin beğeni, kullanım alışkanlıkları ve ihtiyaçlarına
                    göre özelleştirilerek sizlere önerilmesi, şirketimizin ve
                    şirketimizle iş ilişkisi içerisinde olan kişilerin hukuki
                    ve ticari güvenliğinin temini amaçlarıyla işlenecektir.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    3. İşlenen Kişisel Veriler
                  </h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Kimlik Bilgileri (Ad, Soyad)</li>
                    <li>İletişim Bilgileri (Telefon, E-posta, Adres)</li>
                    <li>Müşteri İşlem Bilgileri</li>
                    <li>İşlem Güvenliği Bilgileri</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    4. Kişisel Verilerin Aktarılması
                  </h3>
                  <p>
                    Toplanan kişisel verileriniz, yukarıda belirtilen amaçların
                    gerçekleştirilmesi doğrultusunda iş ortaklarımıza, tedarikçilerimize,
                    kanunen yetkili kamu kurumlarına ve özel kişilere KVKK'nın
                    8. ve 9. maddelerinde belirtilen kişisel veri işleme
                    şartları ve amaçları çerçevesinde aktarılabilecektir.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    5. Kişisel Veri Toplamanın Yöntemi ve Hukuki Sebebi
                  </h3>
                  <p>
                    Kişisel verileriniz, şirketimiz tarafından sunulan hizmetlerden
                    yararlanmanız sırasında, web sitesi, telefon, e-posta gibi
                    kanallar aracılığıyla sözlü, yazılı veya elektronik ortamda
                    toplanmaktadır.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    6. Kişisel Veri Sahibinin Hakları
                  </h3>
                  <p className="mb-3">
                    KVKK'nın 11. maddesi uyarınca, kişisel veri sahipleri:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Kişisel verilerinin işlenip işlenmediğini öğrenme,</li>
                    <li>Kişisel verileri işlenmişse buna ilişkin bilgi talep etme,</li>
                    <li>Kişisel verilerin işlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme,</li>
                    <li>Yurt içinde veya yurt dışında kişisel verilerin aktarıldığı üçüncü kişileri bilme,</li>
                    <li>Kişisel verilerin eksik veya yanlış işlenmiş olması hâlinde bunların düzeltilmesini isteme,</li>
                    <li>KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde kişisel verilerin silinmesini veya yok edilmesini isteme,</li>
                    <li>Kişisel verilerin düzeltilmesi, silinmesi veya yok edilmesi halinde bu işlemlerin kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme,</li>
                    <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkmasına itiraz etme,</li>
                    <li>Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması hâlinde zararın giderilmesini talep etme haklarına sahiptir.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cookie Policy Section */}
            <div className="border-t-2 border-gray-200 pt-16">
              <h2 className="text-3xl font-bold text-[#0A1628] mb-6">
                Çerez Politikası
              </h2>

              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    1. Çerez Nedir?
                  </h3>
                  <p>
                    Çerezler, ziyaret ettiğiniz internet siteleri tarafından
                    tarayıcılar aracılığıyla cihazınıza veya ağ sunucusuna
                    depolanan küçük metin dosyalarıdır. Web sitesini ziyaret
                    ettiğinizde site, tarayıcınız aracılığıyla cihazınıza bir
                    çerez gönderir ve depolar.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    2. Çerez Kullanım Amacı
                  </h3>
                  <p className="mb-3">
                    Web sitemizde çerezler aşağıdaki amaçlarla kullanılmaktadır:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Web sitesinin düzgün çalışmasını sağlamak</li>
                    <li>Kullanıcı deneyimini iyileştirmek</li>
                    <li>Web sitesi performansını analiz etmek</li>
                    <li>Güvenlik önlemlerini uygulamak</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    3. Çerez Türleri
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <strong className="text-[#0A1628]">Zorunlu Çerezler:</strong>
                      <p>Web sitesinin düzgün çalışması için gerekli olan çerezlerdir.</p>
                    </div>
                    <div>
                      <strong className="text-[#0A1628]">Performans Çerezleri:</strong>
                      <p>Web sitesinin performansını ölçmek ve iyileştirmek için kullanılır.</p>
                    </div>
                    <div>
                      <strong className="text-[#0A1628]">Fonksiyonel Çerezler:</strong>
                      <p>Kullanıcı tercihlerini hatırlamak için kullanılır.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    4. Çerez Kontrolü
                  </h3>
                  <p>
                    Tarayıcınızın ayarlarını değiştirerek çerezlere ilişkin
                    tercihlerinizi kişiselleştirme imkânına sahipsiniz.
                    Tarayıcınızın sunduğu ayarları kullanarak çerezleri
                    engelleyebilir, silebilir veya çerez kullanıldığında uyarı
                    alabilirsiniz.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                    5. İletişim
                  </h3>
                  <p>
                    KVKK ve çerez politikası ile ilgili sorularınız için bizimle
                    iletişime geçebilirsiniz:
                  </p>
                  <div className="mt-4 p-6 bg-[#F8F6F3] rounded-xl">
                    <p className="font-semibold text-[#0A1628]">Bursa Sulama Sistemleri</p>
                    <p>Atatürk Caddesi No:72, Hasan Boztürk Apartmanı</p>
                    <p>Görükle / Nilüfer / Bursa</p>
                    <p className="mt-2">
                      <a href="tel:+905071344668" className="text-[#14B8A6] hover:underline">
                        +90 507 134 46 68
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 text-sm text-gray-500">
              <p>Son Güncelleme: {new Date().toLocaleDateString('tr-TR')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default KvkkPage;
